import { type QuestionnaireDefinitionDto, useQuestionnaireDefinition } from "@repo/data-store";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { CatalogDefinition, QuestionnaireRead } from "@/features/catalog";
import { useDefinitionChangePoints, useDefinitionView } from "@/features/catalog/hooks/use-definition-as-of";
import { asOfSearchSchema } from "@/features/catalog/lib/as-of-search";
import { questionnaireDefinitionUrlFromRouteId } from "@/features/catalog/lib/questionnaire-definition-route";

export const Route = createFileRoute("/catalog/questionnaires/$questionnaireId")({
  component: QuestionnaireDetailComponent,
  validateSearch: asOfSearchSchema,
});

function QuestionnaireDetailComponent() {
  const { questionnaireId } = Route.useParams();
  const { asOf } = Route.useSearch();
  const { permissions } = Route.useRouteContext();
  const navigate = useNavigate();
  const definitionUrl = questionnaireDefinitionUrlFromRouteId(questionnaireId);
  const live = useQuestionnaireDefinition(asOf ? undefined : definitionUrl);
  const view = useDefinitionView<QuestionnaireDefinitionDto>(definitionUrl, asOf, live);
  const { changePoints } = useDefinitionChangePoints(definitionUrl);

  if (view.isPending) return <CatalogDefinition.Pending />;
  if (!view.definition) {
    return (
      <CatalogDefinition.NotFound
        label="Questionnaire"
        backTo="/catalog/questionnaires"
        backLabel="Back to questionnaires"
      />
    );
  }

  return (
    <QuestionnaireRead
      key={`${questionnaireId}|${asOf ?? "latest"}`}
      questionnaire={view.definition}
      asOf={asOf}
      changePoints={changePoints}
      canEdit={!view.isPast && permissions.canWrite("Questionnaire")}
      onRevised={() => navigate({ to: "/catalog/questionnaires" })}
    />
  );
}
